import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatDateTime } from '@/i18n/format';
import type { CatalogImportPreview, SkuImportOnExisting } from '@/api/types';
import { KIND_NAME_KEYS } from '@/lib/channels';

/**
 * Import from a connected marketplace: choose the marketplace -> load products -> the page shows the SAME
 * check result as for an Excel file (new / update / unchanged / errors) and the SAME confirm button.
 * Only the source differs; the server runs both through one validation and commit engine. A marketplace
 * whose products cannot be read yet is shown plainly as not available - never as a button that fails.
 */
export function MarketplaceImport(props: {
  onExisting: SkuImportOnExisting;
  disabled: boolean;
  /** The batch the seller has just confirmed (lets «load the next products» appear after it). */
  confirmedBatchId: string | null;
  onPreview: (preview: CatalogImportPreview) => void;
  onError: (error: unknown) => void;
  onClose: () => void;
}): JSX.Element {
  const { t, locale } = useLanguage();
  const providers = useApi(() => endpoints.catalogImportProviders(), []);
  const [channelId, setChannelId] = useState('');
  const [busy, setBusy] = useState(false);
  const [more, setMore] = useState<{ channelId: string; cursor: string; batchId: string } | null>(null);

  if (providers.status === 'loading') return <div className="card" style={{ marginBottom: 16 }}><LoadingState label={t('mpImport.loading')} /></div>;
  if (providers.status === 'error') return <ErrorBanner error={providers.error} onRetry={providers.reload} />;
  const list = providers.data.providers;
  const multiOwner = new Set(list.map((p) => p.ownerId)).size > 1;
  const chosen = list.find((p) => p.channelId === channelId) ?? null;
  const canContinue = more !== null && more.batchId === props.confirmedBatchId;

  async function load(id: string, cursor: string | null): Promise<void> {
    setBusy(true);
    props.onError(null);
    try {
      const p = await endpoints.catalogImportPreview({ channelId: id, onExisting: props.onExisting, ...(cursor ? { cursor } : {}) });
      setMore(p.nextCursor ? { channelId: id, cursor: p.nextCursor, batchId: p.batchId } : null);
      props.onPreview(p);
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" style={{ marginBottom: 16 }} aria-label={t('mpImport.title')}>
      <h2 style={{ marginTop: 0 }}>{t('mpImport.title')}</h2>
      {list.length === 0 ? (
        <>
          <EmptyState title={t('mpImport.noneTitle')} hint={t('mpImport.noneHint')} />
          <div className="form-actions">
            <button type="button" onClick={props.onClose}>{t('mpImport.close')}</button>
          </div>
        </>
      ) : (
        <>
          <p className="sub" style={{ marginTop: 0 }}>{t('mpImport.hint')}</p>
          <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="sub" style={{ padding: 0, marginBottom: 4 }}>{t('mpImport.choose')}</legend>
            {list.map((p) => {
              const name = t(KIND_NAME_KEYS[p.kind]);
              return (
                <div key={p.channelId}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', minHeight: 44, opacity: p.available ? 1 : 0.7 }}>
                    <input
                      type="radio"
                      name="mp-channel"
                      value={p.channelId}
                      checked={channelId === p.channelId}
                      disabled={!p.available || busy}
                      style={{ minHeight: 20, width: 20, height: 20, flex: '0 0 auto' }}
                      onChange={() => setChannelId(p.channelId)}
                    />
                    <span>{multiOwner ? `${name} · ${p.ownerName}` : name}</span>
                  </label>
                  {!p.available && <div className="sub" role="note" style={{ marginLeft: 28, marginBottom: 8 }}>{t('mpImport.unavailable')}</div>}
                  {p.available && p.lastCheckedAt && (
                    <div className="sub" style={{ marginLeft: 28, marginBottom: 8 }}>{t('mpImport.lastCheck', { when: formatDateTime(p.lastCheckedAt, locale) })}</div>
                  )}
                </div>
              );
            })}
          </fieldset>
          <div className="form-actions">
            <button type="button" className="primary" disabled={busy || props.disabled || !chosen} onClick={() => chosen && void load(chosen.channelId, null)}>
              {busy ? t('mpImport.fetching') : t('mpImport.fetch')}
            </button>
            {canContinue && more && (
              <button type="button" disabled={busy || props.disabled} onClick={() => void load(more.channelId, more.cursor)}>{t('mpImport.next')}</button>
            )}
            <button type="button" disabled={busy} onClick={props.onClose}>{t('mpImport.close')}</button>
          </div>
        </>
      )}
    </section>
  );
}
